import { useState, useEffect } from 'react'
import { useT } from '@/i18n'
import { api } from '@/services/api-client'
import { Key, Plus, Trash2, Eye, EyeOff, ToggleLeft, ToggleRight, Loader2 } from 'lucide-react'

type UserKey = {
  id: string
  provider_type: string
  label: string
  is_active: number
  total_requests: number
  total_tokens: number
  rate_limit_pct: number
  created_at: string
}

type ProviderInfo = {
  display_name: string
  models: string[]
}

export function ApiKeysView() {
  const t = useT()
  const [keys, setKeys] = useState<UserKey[]>([])
  const [providers, setProviders] = useState<Record<string, ProviderInfo>>({})
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)

  const [form, setForm] = useState({
    provider_type: 'openai',
    api_key: '',
    label: '',
  })

  const fetchKeys = async () => {
    setLoading(true)
    try {
      const data = await api.getUserKeys()
      setKeys(data.keys)
      setProviders(data.providers)
    } catch {
      /* ignore */
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchKeys() }, [])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.api_key.trim()) return
    setSaving(true)
    try {
      await api.createUserKey({
        provider_type: form.provider_type,
        api_key: form.api_key.trim(),
        label: form.label.trim() || undefined,
      })
      setForm({ provider_type: 'openai', api_key: '', label: '' })
      setShowAdd(false)
      fetchKeys()
    } catch {
      /* ignore */
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (key: UserKey) => {
    try {
      await api.updateUserKey(key.id, { is_active: !key.is_active })
      fetchKeys()
    } catch {
      /* ignore */
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm(t.apiKeysDeleteConfirm)) return
    try {
      await api.deleteUserKey(id)
      fetchKeys()
    } catch {
      /* ignore */
    }
  }

  const toggleVisible = (id: string) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  // Group keys by provider
  const groupedKeys: Record<string, UserKey[]> = {}
  keys.forEach((k) => {
    if (!groupedKeys[k.provider_type]) groupedKeys[k.provider_type] = []
    groupedKeys[k.provider_type].push(k)
  })

  const providerOptions = Object.entries(providers).sort((a, b) => a[1].display_name.localeCompare(b[1].display_name))

  // Usage bar percentage (simulated based on rate_limit_pct)
  const usageBar = (pct: number) => (
    <div className="api-keys-usage-bar">
      <div className="api-keys-usage-fill" style={{ width: `${Math.min(pct, 100)}%`, background: pct > 80 ? '#f85149' : pct > 50 ? '#d29922' : '#3fb950' }} />
      <span className="api-keys-usage-text">{pct}%</span>
    </div>
  )

  return (
    <div className="api-keys">
      <div className="api-keys-header">
        <div className="api-keys-title-row">
          <Key size={24} />
          <div>
            <h1>{t.apiKeysTitle}</h1>
            <p className="api-keys-desc">{t.apiKeysDesc}</p>
          </div>
        </div>
        <button className="api-keys-add-btn" onClick={() => setShowAdd(!showAdd)}>
          <Plus size={16} />
          {t.apiKeysAdd}
        </button>
      </div>

      {showAdd && (
        <form className="api-keys-form" onSubmit={handleAdd}>
          <div className="api-keys-form-grid">
            <label className="api-keys-field">
              <span>{t.apiKeysProvider}</span>
              <select
                value={form.provider_type}
                onChange={(e) => setForm({ ...form, provider_type: e.target.value })}
              >
                {providerOptions.map(([type, info]) => (
                  <option key={type} value={type}>{info.display_name}</option>
                ))}
              </select>
            </label>
            <label className="api-keys-field">
              <span>{t.apiKeysLabel}</span>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="np. Klucz firmowy, Klucz osobisty..."
              />
            </label>
            <label className="api-keys-field api-keys-field--wide">
              <span>{t.apiKeysKey}</span>
              <input
                type="password"
                value={form.api_key}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                placeholder="sk-... / key-..."
                required
              />
            </label>
          </div>
          <button type="submit" className="api-keys-submit-btn" disabled={saving}>
            {saving ? <Loader2 size={14} className="spinner" /> : <Plus size={14} />}
            {t.apiKeysAdd}
          </button>
        </form>
      )}

      {loading ? (
        <div className="api-keys-loading"><Loader2 size={20} className="spinner" /> {t.loading}</div>
      ) : keys.length === 0 ? (
        <div className="api-keys-empty">{t.apiKeysNoKeys}</div>
      ) : (
        <div className="api-keys-groups">
          {Object.entries(groupedKeys).map(([providerType, providerKeys]) => (
            <div key={providerType} className="api-keys-group">
              <div className="api-keys-group-header">
                <h3>{providers[providerType]?.display_name || providerType}</h3>
                <span className="api-keys-group-count">{providerKeys.length} {providerKeys.length === 1 ? 'key' : 'keys'}</span>
              </div>
              <div className="api-keys-list">
                {providerKeys.map((k) => (
                  <div key={k.id} className={`api-keys-card ${k.is_active ? '' : 'api-keys-card--inactive'}`}>
                    <div className="api-keys-card-top">
                      <div className="api-keys-card-info">
                        <span className="api-keys-card-label">{k.label || providerType}</span>
                        <div className="api-keys-card-key">
                          <code>{visibleKeys.has(k.id) ? '••••••••' : '••••••••••••••••'}</code>
                          <button className="api-keys-icon-btn" onClick={() => toggleVisible(k.id)}>
                            {visibleKeys.has(k.id) ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                      </div>
                      <div className="api-keys-card-actions">
                        <button
                          className="api-keys-toggle-btn"
                          onClick={() => handleToggle(k)}
                          title={k.is_active ? t.leasingActive : t.leasingPaused}
                        >
                          {k.is_active ? <ToggleRight size={18} className="api-keys-icon-active" /> : <ToggleLeft size={18} className="api-keys-icon-paused" />}
                        </button>
                        <button className="api-keys-delete-btn" onClick={() => handleDelete(k.id)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="api-keys-card-stats">
                      <div className="api-keys-stat">
                        <span className="api-keys-stat-label">{t.apiKeysUsage}</span>
                        {usageBar(k.rate_limit_pct)}
                      </div>
                      <div className="api-keys-stat">
                        <span className="api-keys-stat-label">{t.apiKeysRequests}</span>
                        <span className="api-keys-stat-value">{k.total_requests.toLocaleString()}</span>
                      </div>
                      <div className="api-keys-stat">
                        <span className="api-keys-stat-label">{t.apiKeysTokens}</span>
                        <span className="api-keys-stat-value">{k.total_tokens.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
